// CopyField — a read-only value with a copy button (API keys, integration
// tokens, connection strings). Hand-rolled (native-owned bucket, [ADR-028]): a
// readonly <input> (selectable → native Ctrl-C works as a fallback) inside the
// shared `field-frame`, plus a copy IconButton and, for secrets, a reveal toggle.
//
// Copy feedback is an inline icon swap (Copy → Check, ~2s) plus a polite
// aria-live announcement — NOT a toast (a transient toast vanishes before
// screen-reader / keyboard users catch it). The value renders in the UI face (no
// monospace — [ADR-031]).

import { useId, useRef, useState, useEffect } from 'react';
import { icons } from './icons.js';
import { useTranslation } from 'react-i18next';

export interface CopyFieldProps {
  /** The value shown and copied (the full value is always what gets copied). */
  value: string;
  /** Visible label rendered above the field. */
  label?: string;
  /** Accessible name for the value when there is no visible `label`. */
  ariaLabel?: string;
  /** Mask the value (••••) behind a reveal toggle — for API keys / secrets. */
  secret?: boolean;
  className?: string;
}

const MASK = '••••••••••••••••';
const RESET_MS = 2000;

// p-2 + an 18px icon gives a ≥34px hit target (WCAG 2.2 SC 2.5.8 ≥ 24) without
// relying on min-width (an arbitrary min-w- value can miss the content scan).
const trailingBtn =
  'inline-flex items-center justify-center p-2 rounded-md ' +
  'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] ' +
  'hover:bg-[var(--color-bg-hover)] outline-none focus-ring-inset transition-colors';

export function CopyField({ value, label, ariaLabel, secret = false, className = '' }: CopyFieldProps) {
  const { t } = useTranslation('common');
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => () => clearTimeout(timer.current), []);

  const display = secret && !revealed ? MASK : value;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), RESET_MS);
    } catch {
      // Non-secure context or no Clipboard API: select the value so the user can
      // copy manually with the keyboard. Never flash a false "Copied".
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  };

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-[var(--color-text-primary)]">
          {label}
        </label>
      )}
      <div className="flex items-center rounded-md field-frame transition-colors">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          readOnly
          dir="ltr"
          value={display}
          aria-label={label ? undefined : ariaLabel}
          onFocus={(e) => e.currentTarget.select()}
          className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 text-sm pointer-coarse:text-base text-[var(--color-text-primary)] outline-none"
        />
        <div className="flex items-center gap-0.5 pe-1">
          {secret && (
            <button
              type="button"
              aria-label={revealed ? t('actions.hideValue') : t('actions.revealValue')}
              aria-pressed={revealed}
              onClick={() => setRevealed((v) => !v)}
              className={trailingBtn}
            >
              {revealed ? <icons.hide aria-hidden size={18} /> : <icons.reveal aria-hidden size={18} />}
            </button>
          )}
          <button
            type="button"
            aria-label={copied ? t('actions.copied') : t('actions.copy')}
            onClick={copy}
            className={trailingBtn}
          >
            {copied ? (
              <icons.check aria-hidden size={18} className="text-[var(--color-success)]" />
            ) : (
              <icons.copy aria-hidden size={18} />
            )}
          </button>
        </div>
      </div>
      {/* Polite announcement so the icon swap is not the sole (visual-only) signal. */}
      <span className="sr-only" aria-live="polite">
        {copied ? t('actions.copied') : ''}
      </span>
    </div>
  );
}

// InlineCopy — the SAME primitive's inline face, on the sites `CopyableCode`
// mechanics (the landed exemplar in `sitesDisplay`, which consolidates onto
// this at the peek-uplift pass): the WHOLE value is the copy button (mono,
// LTR), the copy glyph reveals on hover / focus of the value (list density) —
// pass `iconAlways` on detail surfaces (the peek). Click stops propagation so
// a row-activate host (DataTable → peek) never swallows the copy. Feedback =
// icon swap + polite aria-live, never a toast. CopyField above stays the
// token/secret surface.
export interface InlineCopyProps {
  value: string;
  /** Always show the copy glyph (peeks / detail surfaces); default hover/focus-revealed. */
  iconAlways?: boolean;
  /** Where the value gives way when it doesn't fit. `end` (default) is CSS
   *  ellipsis. `middle` keeps the HEAD and the TAIL — the right choice for
   *  identifiers whose tail is what distinguishes them (serial numbers), where
   *  an end-ellipsis would hide the only differing characters. The full value
   *  stays in the copied text, the `title`, and the accessible name. */
  truncate?: 'end' | 'middle';
  /** Classes on the button (font utilities style the value — defaults mono/xs). */
  className?: string;
}

/** Keep `head` leading + `tail` trailing characters, eliding the middle. Only
 *  shortens when it actually saves characters — a short serial is left alone. */
function middleTruncate(value: string, head = 8, tail = 6): string {
  return value.length <= head + tail + 1 ? value : `${value.slice(0, head)}…${value.slice(-tail)}`;
}

export function InlineCopy({
  value,
  iconAlways = false,
  truncate = 'end',
  className = 'font-mono text-xs',
}: InlineCopyProps) {
  const { t } = useTranslation('common');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), RESET_MS);
    } catch {
      // No Clipboard API (non-secure context): never flash a false "Copied".
    }
  };

  return (
    <button
      type="button"
      aria-label={copied ? t('actions.copied') : t('actions.copy')}
      onClick={(e) => {
        e.stopPropagation();
        void copy();
      }}
      title={value}
      className={`group/copy inline-flex min-w-0 cursor-pointer items-center gap-1 rounded text-[var(--color-text-secondary)] outline-none transition-colors focus-ring hover:text-[var(--color-text-primary)] ${className}`}
    >
      <span className="min-w-0 truncate" dir="ltr">
        {truncate === 'middle' ? middleTruncate(value) : value}
      </span>
      {copied ? (
        <icons.check aria-hidden size={14} className="shrink-0 text-[var(--color-success)]" />
      ) : (
        <icons.copy
          aria-hidden
          size={14}
          className={`shrink-0 ${iconAlways ? '' : 'opacity-0 transition-opacity group-hover/copy:opacity-100 group-focus-visible/copy:opacity-100'}`}
        />
      )}
      <span className="sr-only" aria-live="polite">
        {copied ? t('actions.copied') : ''}
      </span>
    </button>
  );
}
